/**
 * Máy chủ giả của màn Token AI: `/api/v1/tenants/{id}/ai-limits`, `/ai-token-quota/daily`, `/ai-token-quota/breakdown`
 * (ADR-0123 mục 6). Màu và nhãn không ở đây (`modules/token-ai/constants.ts`).
 *
 * Tình huống mẫu: một hạn mức bình thường, một sắp hết, một chạm trần, một vượt (chỉ cảnh báo), một đang tắt, một của
 * người đã rời tổ chức (`ten_khoa: null`). Tạo trùng phạm vi, đối tượng, tính năng, chu kỳ nhận `409`.
 */
import type { ApiError } from '../lib/errors';
import type {
  AiLimit,
  AiLimitCreate,
  AiLimitUpdate,
  ChuKyHanMucAi,
  DongPhanRa,
  PhamViHanMucAi,
  SuDungNgay,
  TheoPhanRa,
} from '../modules/token-ai/types';
import { TI_LE_DAU_RA } from './goi-nap-token';
import { mayChuNguoiDung } from './nguoi-dung';
import { mayChuPhongBan } from './phong-ban';

const hai = (n: number) => String(n).padStart(2, '0');
const LECH_VN = 7 * 3_600_000;

function moLai(chuKy: ChuKyHanMucAi): string {
  const vn = new Date(Date.now() + LECH_VN);
  const y = vn.getUTCFullYear();
  const m = vn.getUTCMonth();
  const d = vn.getUTCDate();
  const moc =
    chuKy === 'NGAY'
      ? new Date(Date.UTC(y, m, d + 1))
      : chuKy === 'TUAN'
        ? new Date(Date.UTC(y, m, d + (8 - (vn.getUTCDay() || 7))))
        : new Date(Date.UTC(y, m + 1, 1));
  return `${moc.getUTCFullYear()}-${hai(moc.getUTCMonth() + 1)}-${hai(moc.getUTCDate())}T00:00:00+07:00`;
}

const TAO_LUC = '2026-10-12T02:30:00Z';

function dong(
  id: string,
  pham_vi: PhamViHanMucAi,
  khoa: string,
  ten_khoa: string | null,
  tinh_nang: string | null,
  chu_ky: ChuKyHanMucAi,
  gioi_han: number,
  da_dung: number,
  hanh_dong: AiLimit['hanh_dong'],
  bat = true,
): AiLimit {
  return {
    id,
    pham_vi,
    khoa,
    ten_khoa,
    tinh_nang,
    chu_ky,
    gioi_han,
    hanh_dong,
    bat,
    da_dung,
    con_lai: Math.max(0, gioi_han - da_dung),
    resets_at: moLai(chu_ky),
    created_at: TAO_LUC,
    updated_at: TAO_LUC,
  };
}

const tenNguoi = (i: number) => mayChuNguoiDung.danhSach({ limit: 100 }).items[i];

let cacHanMuc: AiLimit[] | null = null;
function hanMuc(): AiLimit[] {
  if (!cacHanMuc) {
    const a = tenNguoi(1);
    const b = tenNguoi(2);
    cacHanMuc = [
      dong('lim-1', 'DON_VI', 'pb-ke-toan', 'Phòng Kế toán', null, 'THANG', 200_000, 96_200, 'CHAN'),
      dong('lim-2', 'DON_VI', 'pb-kd', 'Khối Kinh doanh', 'tro-ly-ai', 'TUAN', 50_000, 44_300, 'CHAN'),
      dong('lim-3', 'NGUOI_DUNG', a?.user_id ?? 'u-1', a?.display_name ?? null, null, 'NGAY', 20_000, 20_000, 'CHAN'),
      dong('lim-4', 'NGUOI_DUNG', b?.user_id ?? 'u-2', b?.display_name ?? null, 'ocr', 'THANG', 100_000, 1_227_000, 'CANH_BAO'),
      dong('lim-5', 'DON_VI', 'pb-bgd', 'Ban Giám đốc', null, 'THANG', 500_000, 12_000, 'CANH_BAO', false),
      dong('lim-6', 'NGUOI_DUNG', '7f3a9c1e-0000-4000-8000-000000000042', null, null, 'THANG', 30_000, 4_100, 'CHAN'),
    ];
  }
  return cacHanMuc;
}

function loi(status: number, code: string, message: string): ApiError {
  return { status, code, message };
}

function tenKhoa(pham_vi: PhamViHanMucAi, khoa: string): string | null {
  if (pham_vi === 'DON_VI') return mayChuPhongBan.danhSach({ limit: 100 }).items.find((p) => p.id === khoa)?.name ?? null;
  return mayChuNguoiDung.danhSach({ limit: 100 }).items.find((u) => u.user_id === khoa)?.display_name ?? null;
}

function kiemTrung(ds: AiLimit[], k: Pick<AiLimit, 'pham_vi' | 'khoa' | 'tinh_nang' | 'chu_ky'>, boQua?: string): void {
  const trung = ds.some(
    (h) =>
      h.id !== boQua && h.pham_vi === k.pham_vi && h.khoa === k.khoa && h.tinh_nang === k.tinh_nang && h.chu_ky === k.chu_ky,
  );
  if (trung) throw loi(409, 'AI_LIMIT_CONFLICT', 'Đã có hạn mức cùng đối tượng, tính năng và chu kỳ. Hãy sửa hạn mức đó.');
}

/** Tỉ trọng theo thứ trong tuần, cố định theo ngày để biểu đồ không đổi giữa các lần tải. */
function trongSo(ngay: string): number {
  const d = new Date(`${ngay}T00:00:00Z`);
  const nen = [0.18, 1, 1.1, 0.95, 1.2, 1.05, 0.3][d.getUTCDay()] ?? 1;
  return nen * (0.75 + ((d.getUTCDate() * 37) % 50) / 100);
}

const PHAN_RA: Record<TheoPhanRa, readonly (readonly [string | null, string | null, number])[]> = {
  TINH_NANG: [
    ['tro-ly-ai', 'Trợ lý AI', 0.41],
    ['ocr', 'Nhận dạng OCR', 0.24],
    ['tham-dinh', 'Thẩm định hồ sơ', 0.13],
    ['tom-tat', 'Tóm tắt văn bản', 0.09],
    ['soan-thao', 'Soạn thảo văn bản', 0.06],
    ['bao-cao', 'Báo cáo doanh nghiệp', 0.03],
    ['tri-thuc', 'Tri thức', 0.02],
    ['ekyc', 'Định danh eKYC', 0.01],
    ['rpa', 'Trợ lý RPA', 0.006],
    [null, null, 0.004],
  ],
  DON_VI: [
    ['pb-ke-toan', 'Phòng Kế toán', 0.32],
    ['pb-kd-mb', 'Phòng Kinh doanh miền Bắc', 0.22],
    ['pb-kd-mn', 'Phòng Kinh doanh miền Nam', 0.18],
    ['pb-thue', 'Tổ Thuế', 0.11],
    ['pb-bgd', 'Ban Giám đốc', 0.07],
    ['pb-ngan-quy', 'Phòng Ngân quỹ', 0.06],
    [null, null, 0.04],
  ],
  NGUOI_DUNG: [
    ['u-a', 'Nguyễn Thị Phương Thảo Quỳnh Anh', 0.19],
    ['u-b', 'Trần Minh Quân', 0.16],
    ['u-c', 'Phạm Thu Hà', 0.13],
    ['u-d', 'Lê Hoàng Long', 0.11],
    ['u-e', 'Hoàng Đức Anh', 0.09],
    ['u-f', 'Bùi Thị Mai', 0.07],
    ['u-g', 'Đỗ Văn Khoa', 0.05],
    ['u-h', 'Ngô Bảo Châu', 0.04],
    ['u-i', 'Lý Thị Hồng', 0.03],
    ['u-j', 'Vũ Đình Nam', 0.02],
    ['u-k', 'Đặng Thu Trang', 0.02],
    [null, null, 0.09],
  ],
};

function chia(fox: number): { fox_dau_vao: number; fox_dau_ra: number } {
  const ra = Math.round(fox * TI_LE_DAU_RA);
  return { fox_dau_vao: fox - ra, fox_dau_ra: ra };
}

export const mayChuTokenAi = {
  danhSachHanMuc(phamVi?: PhamViHanMucAi): { items: AiLimit[] } {
    return { items: structuredClone(hanMuc().filter((h) => !phamVi || h.pham_vi === phamVi)) };
  },

  taoHanMuc(du: AiLimitCreate): AiLimit {
    const ds = hanMuc();
    kiemTrung(ds, du);
    const moi = dong(
      `lim-${Date.now()}`,
      du.pham_vi,
      du.khoa,
      tenKhoa(du.pham_vi, du.khoa),
      du.tinh_nang,
      du.chu_ky,
      du.gioi_han,
      0,
      du.hanh_dong,
      du.bat,
    );
    ds.unshift(moi);
    return structuredClone(moi);
  },

  suaHanMuc(id: string, du: AiLimitUpdate): AiLimit {
    const ds = hanMuc();
    const i = ds.findIndex((h) => h.id === id);
    const cu = ds[i];
    if (!cu) throw loi(404, 'NOT_FOUND', 'Không tìm thấy hạn mức này.');
    const moi = { ...cu, ...du, updated_at: new Date().toISOString() };
    kiemTrung(ds, moi, id);
    moi.con_lai = Math.max(0, moi.gioi_han - moi.da_dung);
    moi.resets_at = moLai(moi.chu_ky);
    ds[i] = moi;
    return structuredClone(moi);
  },

  xoaHanMuc(id: string): void {
    const ds = hanMuc();
    const i = ds.findIndex((h) => h.id === id);
    if (i < 0) throw loi(404, 'NOT_FOUND', 'Không tìm thấy hạn mức này.');
    ds.splice(i, 1);
  },

  /** Các ngày của tháng hiện tại cộng lại bằng `tongThang`, khớp với số đã dùng của tháng. */
  theoNgay(from: string, to: string, tongThang: number): { items: SuDungNgay[] } {
    const homNay = new Date(Date.now() + LECH_VN).toISOString().slice(0, 10);
    const thangNay = homNay.slice(0, 7);
    let tongTrongSo = 0;
    for (let d = 1; d <= Number(homNay.slice(8, 10)); d += 1) tongTrongSo += trongSo(`${thangNay}-${hai(d)}`);
    const items: SuDungNgay[] = [];
    const cuoi = Date.parse(`${to}T00:00:00Z`);
    for (let ms = Date.parse(`${from}T00:00:00Z`); ms <= cuoi; ms += 86_400_000) {
      const ngay = new Date(ms).toISOString().slice(0, 10);
      if (ngay > homNay) break;
      const fox = Math.round((tongThang / Math.max(tongTrongSo, 1)) * trongSo(ngay) * (ngay.startsWith(thangNay) ? 1 : 0.2));
      items.push({ ngay, ...chia(fox), calls: Math.max(1, Math.round(fox / 4_000)) });
    }
    return { items };
  },

  phanRa(thang: string, theo: TheoPhanRa, tongThang: number): { items: DongPhanRa[] } {
    const thangNay = new Date(Date.now() + LECH_VN).toISOString().slice(0, 7);
    const tong = thang === thangNay ? tongThang : Math.round(tongThang * 0.8);
    return {
      items: PHAN_RA[theo].map(([khoa, ten, ti]) => {
        const fox = Math.round(tong * ti);
        return { khoa, ten, ...chia(fox), fox_tokens: fox, calls: Math.max(1, Math.round(fox / 4_000)) };
      }),
    };
  },
};
